import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classPrivateFieldGet from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classPrivateFieldGet.js";
import _classPrivateFieldSet from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classPrivateFieldSet.js";
var _queryCache, _mutationCache, _defaultOptions, _queryDefaults, _mutationDefaults, _mountCount, _unsubscribeFocus, _unsubscribeOnline;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
// src/queryClient.ts
import { functionalUpdate, hashKey, hashQueryKeyByOptions, noop, partialMatchKey, resolveStaleTime, skipToken } from "./utils.js";
import { QueryCache } from "./queryCache.js";
import { MutationCache } from "./mutationCache.js";
import { focusManager } from "./focusManager.js";
import { onlineManager } from "./onlineManager.js";
import { notifyManager } from "./notifyManager.js";
import { infiniteQueryBehavior } from "./infiniteQueryBehavior.js";
var QueryClient = (_queryCache = /*#__PURE__*/new WeakMap(), _mutationCache = /*#__PURE__*/new WeakMap(), _defaultOptions = /*#__PURE__*/new WeakMap(), _queryDefaults = /*#__PURE__*/new WeakMap(), _mutationDefaults = /*#__PURE__*/new WeakMap(), _mountCount = /*#__PURE__*/new WeakMap(), _unsubscribeFocus = /*#__PURE__*/new WeakMap(), _unsubscribeOnline = /*#__PURE__*/new WeakMap(), class QueryClient {
  constructor() {
    let config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classPrivateFieldInitSpec(this, _queryCache, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _mutationCache, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _defaultOptions, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _queryDefaults, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _mutationDefaults, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _mountCount, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _unsubscribeFocus, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldInitSpec(this, _unsubscribeOnline, {
      writable: true,
      value: void 0
    });
    _classPrivateFieldSet(this, _queryCache, config.queryCache || new QueryCache());
    _classPrivateFieldSet(this, _mutationCache, config.mutationCache || new MutationCache());
    _classPrivateFieldSet(this, _defaultOptions, config.defaultOptions || {});
    _classPrivateFieldSet(this, _queryDefaults, /* @__PURE__ */new Map());
    _classPrivateFieldSet(this, _mutationDefaults, /* @__PURE__ */new Map());
    _classPrivateFieldSet(this, _mountCount, 0);
  }
  mount() {
    var _this$mountCount, _this$mountCount2;
    _classPrivateFieldSet(this, _mountCount, (_this$mountCount = _classPrivateFieldGet(this, _mountCount), _this$mountCount2 = _this$mountCount++, _this$mountCount)), _this$mountCount2;
    if (_classPrivateFieldGet(this, _mountCount) !== 1) return;
    _classPrivateFieldSet(this, _unsubscribeFocus, focusManager.subscribe(async focused => {
      if (focused) {
        await this.resumePausedMutations();
        _classPrivateFieldGet(this, _queryCache).onFocus();
      }
    }));
    _classPrivateFieldSet(this, _unsubscribeOnline, onlineManager.subscribe(async online => {
      if (online) {
        await this.resumePausedMutations();
        _classPrivateFieldGet(this, _queryCache).onOnline();
      }
    }));
  }
  unmount() {
    var _this$mountCount3, _this$mountCount4, _classPrivateFieldGet2, _classPrivateFieldGet3;
    _classPrivateFieldSet(this, _mountCount, (_this$mountCount3 = _classPrivateFieldGet(this, _mountCount), _this$mountCount4 = _this$mountCount3--, _this$mountCount3)), _this$mountCount4;
    if (_classPrivateFieldGet(this, _mountCount) !== 0) return;
    (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _unsubscribeFocus)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.call(this);
    _classPrivateFieldSet(this, _unsubscribeFocus, void 0);
    (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _unsubscribeOnline)) === null || _classPrivateFieldGet3 === void 0 || _classPrivateFieldGet3.call(this);
    _classPrivateFieldSet(this, _unsubscribeOnline, void 0);
  }
  isFetching(filters) {
    return _classPrivateFieldGet(this, _queryCache).findAll(_objectSpread(_objectSpread({}, filters), {}, {
      fetchStatus: "fetching"
    })).length;
  }
  isMutating(filters) {
    return _classPrivateFieldGet(this, _mutationCache).findAll(_objectSpread(_objectSpread({}, filters), {}, {
      status: "pending"
    })).length;
  }
  /**
   * Imperative (non-reactive) way to retrieve data for a QueryKey.
   * Should only be used in callbacks or functions where reading the latest data is necessary, e.g. for optimistic updates.
   *
   * Hint: Do not use this function inside a component, because it won't receive updates.
   * Use `useQuery` to create a `QueryObserver` that subscribes to changes.
   */
  getQueryData(queryKey) {
    var _classPrivateFieldGet4;
    const options = this.defaultQueryOptions({
      queryKey
    });
    return (_classPrivateFieldGet4 = _classPrivateFieldGet(this, _queryCache).get(options.queryHash)) === null || _classPrivateFieldGet4 === void 0 ? void 0 : _classPrivateFieldGet4.state.data;
  }
  ensureQueryData(options) {
    const defaultedOptions = this.defaultQueryOptions(options);
    const query = _classPrivateFieldGet(this, _queryCache).build(this, defaultedOptions);
    const cachedData = query.state.data;
    if (cachedData === void 0) {
      return this.fetchQuery(options);
    }
    if (options.revalidateIfStale && query.isStaleByTime(resolveStaleTime(defaultedOptions.staleTime, query))) {
      void this.prefetchQuery(defaultedOptions);
    }
    return Promise.resolve(cachedData);
  }
  getQueriesData(filters) {
    return _classPrivateFieldGet(this, _queryCache).findAll(filters).map(_ref => {
      let {
        queryKey,
        state
      } = _ref;
      const data = state.data;
      return [queryKey, data];
    });
  }
  setQueryData(queryKey, updater, options) {
    const defaultedOptions = this.defaultQueryOptions({
      queryKey
    });
    const query = _classPrivateFieldGet(this, _queryCache).get(defaultedOptions.queryHash);
    const prevData = query === null || query === void 0 ? void 0 : query.state.data;
    const data = functionalUpdate(updater, prevData);
    if (data === void 0) {
      return void 0;
    }
    return _classPrivateFieldGet(this, _queryCache).build(this, defaultedOptions).setData(data, _objectSpread(_objectSpread({}, options), {}, {
      manual: true
    }));
  }
  setQueriesData(filters, updater, options) {
    return notifyManager.batch(() => _classPrivateFieldGet(this, _queryCache).findAll(filters).map(_ref2 => {
      let {
        queryKey
      } = _ref2;
      return [queryKey, this.setQueryData(queryKey, updater, options)];
    }));
  }
  getQueryState(queryKey) {
    var _classPrivateFieldGet5;
    const options = this.defaultQueryOptions({
      queryKey
    });
    return (_classPrivateFieldGet5 = _classPrivateFieldGet(this, _queryCache).get(options.queryHash)) === null || _classPrivateFieldGet5 === void 0 ? void 0 : _classPrivateFieldGet5.state;
  }
  removeQueries(filters) {
    const queryCache = _classPrivateFieldGet(this, _queryCache);
    notifyManager.batch(() => {
      queryCache.findAll(filters).forEach(query => {
        queryCache.remove(query);
      });
    });
  }
  resetQueries(filters, options) {
    const queryCache = _classPrivateFieldGet(this, _queryCache);
    return notifyManager.batch(() => {
      queryCache.findAll(filters).forEach(query => {
        query.reset();
      });
      return this.refetchQueries(_objectSpread({
        type: "active"
      }, filters), options);
    });
  }
  cancelQueries(filters) {
    let cancelOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const defaultedCancelOptions = _objectSpread({
      revert: true
    }, cancelOptions);
    const promises = notifyManager.batch(() => _classPrivateFieldGet(this, _queryCache).findAll(filters).map(query => query.cancel(defaultedCancelOptions)));
    return Promise.all(promises).then(noop).catch(noop);
  }
  invalidateQueries(filters) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return notifyManager.batch(() => {
      var _ref3, _filters$refetchType;
      _classPrivateFieldGet(this, _queryCache).findAll(filters).forEach(query => {
        query.invalidate();
      });
      if ((filters === null || filters === void 0 ? void 0 : filters.refetchType) === "none") {
        return Promise.resolve();
      }
      return this.refetchQueries(_objectSpread(_objectSpread({}, filters), {}, {
        type: (_ref3 = (_filters$refetchType = filters === null || filters === void 0 ? void 0 : filters.refetchType) !== null && _filters$refetchType !== void 0 ? _filters$refetchType : filters === null || filters === void 0 ? void 0 : filters.type) !== null && _ref3 !== void 0 ? _ref3 : "active"
      }), options);
    });
  }
  refetchQueries(filters) {
    var _options$cancelRefetc;
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const fetchOptions = _objectSpread(_objectSpread({}, options), {}, {
      cancelRefetch: (_options$cancelRefetc = options.cancelRefetch) !== null && _options$cancelRefetc !== void 0 ? _options$cancelRefetc : true
    });
    const promises = notifyManager.batch(() => _classPrivateFieldGet(this, _queryCache).findAll(filters).filter(query => !query.isDisabled() && !query.isStatic()).map(query => {
      let promise = query.fetch(void 0, fetchOptions);
      if (!fetchOptions.throwOnError) {
        promise = promise.catch(noop);
      }
      return query.state.fetchStatus === "paused" ? Promise.resolve() : promise;
    }));
    return Promise.all(promises).then(noop);
  }
  fetchQuery(options) {
    const defaultedOptions = this.defaultQueryOptions(options);
    if (defaultedOptions.retry === void 0) {
      defaultedOptions.retry = false;
    }
    const query = _classPrivateFieldGet(this, _queryCache).build(this, defaultedOptions);
    return query.isStaleByTime(resolveStaleTime(defaultedOptions.staleTime, query)) ? query.fetch(defaultedOptions) : Promise.resolve(query.state.data);
  }
  prefetchQuery(options) {
    return this.fetchQuery(options).then(noop).catch(noop);
  }
  fetchInfiniteQuery(options) {
    options.behavior = infiniteQueryBehavior(options.pages);
    return this.fetchQuery(options);
  }
  prefetchInfiniteQuery(options) {
    return this.fetchInfiniteQuery(options).then(noop).catch(noop);
  }
  ensureInfiniteQueryData(options) {
    options.behavior = infiniteQueryBehavior(options.pages);
    return this.ensureQueryData(options);
  }
  resumePausedMutations() {
    if (onlineManager.isOnline()) {
      return _classPrivateFieldGet(this, _mutationCache).resumePausedMutations();
    }
    return Promise.resolve();
  }
  getQueryCache() {
    return _classPrivateFieldGet(this, _queryCache);
  }
  getMutationCache() {
    return _classPrivateFieldGet(this, _mutationCache);
  }
  getDefaultOptions() {
    return _classPrivateFieldGet(this, _defaultOptions);
  }
  setDefaultOptions(options) {
    _classPrivateFieldSet(this, _defaultOptions, options);
  }
  setQueryDefaults(queryKey, options) {
    _classPrivateFieldGet(this, _queryDefaults).set(hashKey(queryKey), {
      queryKey,
      defaultOptions: options
    });
  }
  getQueryDefaults(queryKey) {
    const defaults = [..._classPrivateFieldGet(this, _queryDefaults).values()];
    const result = {};
    defaults.forEach(queryDefault => {
      if (partialMatchKey(queryKey, queryDefault.queryKey)) {
        Object.assign(result, queryDefault.defaultOptions);
      }
    });
    return result;
  }
  setMutationDefaults(mutationKey, options) {
    _classPrivateFieldGet(this, _mutationDefaults).set(hashKey(mutationKey), {
      mutationKey,
      defaultOptions: options
    });
  }
  getMutationDefaults(mutationKey) {
    const defaults = [..._classPrivateFieldGet(this, _mutationDefaults).values()];
    const result = {};
    defaults.forEach(queryDefault => {
      if (partialMatchKey(mutationKey, queryDefault.mutationKey)) {
        Object.assign(result, queryDefault.defaultOptions);
      }
    });
    return result;
  }
  defaultQueryOptions(options) {
    if (options._defaulted) {
      return options;
    }
    const defaultedOptions = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _defaultOptions).queries), this.getQueryDefaults(options.queryKey)), options), {}, {
      _defaulted: true
    });
    if (!defaultedOptions.queryHash) {
      defaultedOptions.queryHash = hashQueryKeyByOptions(defaultedOptions.queryKey, defaultedOptions);
    }
    if (defaultedOptions.refetchOnReconnect === void 0) {
      defaultedOptions.refetchOnReconnect = defaultedOptions.networkMode !== "always";
    }
    if (defaultedOptions.throwOnError === void 0) {
      defaultedOptions.throwOnError = !!defaultedOptions.suspense;
    }
    if (!defaultedOptions.networkMode && defaultedOptions.persister) {
      defaultedOptions.networkMode = "offlineFirst";
    }
    if (defaultedOptions.queryFn === skipToken) {
      defaultedOptions.enabled = false;
    }
    return defaultedOptions;
  }
  defaultMutationOptions(options) {
    if (options !== null && options !== void 0 && options._defaulted) {
      return options;
    }
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _defaultOptions).mutations), (options === null || options === void 0 ? void 0 : options.mutationKey) && this.getMutationDefaults(options.mutationKey)), options), {}, {
      _defaulted: true
    });
  }
  clear() {
    _classPrivateFieldGet(this, _queryCache).clear();
    _classPrivateFieldGet(this, _mutationCache).clear();
  }
});
export { QueryClient };